import type { Key } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, type FormInstance, Popconfirm, type TableColumnsType } from 'antd';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type { ProdutoComposicaoBody } from '@/features/produto/types/ProdutoComposicao';
import { InputNumber } from '@/components/input/InputNumber';
import { TableDragHandle } from '@/components/table/drag/TableDragHandle';
import { numberToCurrency } from '@/std/format';
import { roundAbnt } from '@/std/math';

export function getColumnsKit({
    form,
    onDelete,
    recalcularPrecoCusto,
    setFormDirty,
}: {
    form: FormInstance<FormProdutoValues>;
    onDelete: (key: Key) => void;
    recalcularPrecoCusto: () => void;
    setFormDirty: (dirty: boolean) => void;
}): TableColumnsType<ProdutoComposicaoBody> {
    return [
        {
            align: 'center',
            key: 'sort',
            render: () => <TableDragHandle />,
            title: 'Mover',
            width: 80,
        },
        {
            align: 'center',
            dataIndex: 'prc_ordem',
            title: 'Ordem',
            width: '6rem',
        },
        {
            children: [
                {
                    dataIndex: ['produto', 'pro_descricao'],
                    title: 'Produto',
                },
                {
                    align: 'center',
                    dataIndex: ['produto', 'unidade', 'pru_sigla'],
                    title: 'Un.',
                    width: '6rem',
                },
                {
                    align: 'center',
                    dataIndex: ['produto', 'pro_preco_custo'],
                    render: numberToCurrency,
                    title: 'Custo',
                    width: '8rem',
                },
                {
                    align: 'center',
                    dataIndex: ['produto', 'pro_preco_venda'],
                    render: numberToCurrency,
                    title: 'Venda',
                    width: '8rem',
                },
            ],
            title: 'Dados do produto',
        },
        {
            align: 'center',
            dataIndex: 'prc_quantidade',
            render: (value, _, index) => {
                const pro_permite_venda_fracionada = form.getFieldValue([
                    'produto_composicao',
                    index,
                    'produto',
                    'pro_permite_venda_fracionada',
                ]);

                const permiteVendaFracionada = pro_permite_venda_fracionada === 1;

                return (
                    <InputNumber
                        decimalScale={permiteVendaFracionada ? 4 : 0}
                        onChange={(value) => {
                            form.setFieldValue(
                                ['produto_composicao', index, 'prc_quantidade'],
                                value,
                            );

                            setFormDirty(true);
                            recalcularPrecoCusto();
                        }}
                        value={value}
                    />
                );
            },
            title: 'Quantidade',
            width: '6rem',
        },
        {
            children: [
                {
                    align: 'center',
                    dataIndex: 'prc_rateio_percentual',
                    render: (value, _, index) => (
                        <InputNumber
                            decimalScale={4}
                            onChange={(value) => {
                                const precoVenda = form.getFieldValue('pro_preco_venda') ?? 0;
                                const percentual = value ?? 0;
                                const totalItemKit = roundAbnt((precoVenda * percentual) / 100, 4);

                                form.setFields([
                                    {
                                        name: [
                                            'produto_composicao',
                                            index,
                                            'prc_rateio_percentual',
                                        ],
                                        value,
                                    },
                                    {
                                        name: ['produto_composicao', index, 'totalItemKit'],
                                        value: totalItemKit,
                                    },
                                ]);

                                setFormDirty(true);
                            }}
                            type='%'
                            value={value}
                        />
                    ),
                    title: '% do item no kit',
                    width: '10rem',
                },
                {
                    align: 'center',
                    dataIndex: 'totalItemKit',
                    render: (value, _, index) => (
                        <InputNumber
                            decimalScale={4}
                            onChange={(value) => {
                                const precoVenda = form.getFieldValue('pro_preco_venda') ?? 0;
                                const totalItemKit = value ?? 0;
                                const percentual = precoVenda
                                    ? roundAbnt((totalItemKit / precoVenda) * 100, 4)
                                    : 0;

                                form.setFields([
                                    {
                                        name: [
                                            'produto_composicao',
                                            index,
                                            'prc_rateio_percentual',
                                        ],
                                        value: percentual,
                                    },
                                    {
                                        name: ['produto_composicao', index, 'totalItemKit'],
                                        value: value,
                                    },
                                ]);

                                setFormDirty(true);
                            }}
                            type='$'
                            value={value}
                        />
                    ),
                    title: 'Total do item no kit',
                    width: '10rem',
                },
            ],
            title: 'Formação do valor do kit',
        },
        {
            align: 'center',
            key: 'actions',
            render: (_, row) => (
                <Popconfirm
                    description='Ao excluir este item, os itens restantes terão seus percentuais recalculados para fechar em 100%.'
                    onConfirm={() => onDelete(row.key)}
                    title='Atenção'
                >
                    <Button danger={true} icon={<DeleteOutlined />} size='small' />
                </Popconfirm>
            ),

            title: 'Ações',
            width: '6rem',
        },
    ];
}
